import clsx from "clsx";
import {
  ElementType,
  FC,
  ReactElement,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import toast, { Renderable } from "react-hot-toast";
import { Trans } from "react-i18next";
import { Tooltip } from "react-tooltip";
import {
  AVAX,
  BSC,
  InsufficientAllowanceError,
  Msg,
  MsgErc20IncreaseAllowance,
  MsgErc20ResetAllowance,
  Network,
  Simulation,
  TxError,
  TxResult,
  gasToken,
  networkTxLink,
  translateError,
} from "rujira.js";

import { useTranslation } from "../../i18n";
import { AccountProvider } from "../../wallets";
import { Animated } from "../icons/Animated";
import { Loader } from "../loader/Loader";
import { Warning } from "../notices/Warning";
import { Decimal } from "../numbers/Decimal";
import { Button, ButtonProps } from "./Button";
import { transactionMessageKey } from "./transactionMessageKey";

// Helper to identify spacing/layout classes
const SPACING_CLASS_REGEX = /^(m|p)(l|r|t|b|x|y)?-\d+$/;
const isSpacingClass = (className: string) =>
  SPACING_CLASS_REGEX.test(className);

// Separate spacing classes from styling classes
const separateClasses = (className?: string) => {
  const classes = className?.split(" ") || [];
  return {
    spacing: classes.filter(isSpacingClass),
    styling: classes.filter((c) => !isSpacingClass(c)),
  };
};

export type SimulationState = {
  simulation: Simulation | undefined;
  /** All transaction-stage fees, grouped by asset. */
  simulations?: Simulation[];
  status: "none" | "running" | "completed" | "failed";
};

export type PendingTransaction = {
  id: number;
  status: "pending" | "confirmed" | "failed";
  txHash?: string;
  message?: string;
  finalTransaction?: boolean;
};

type PendingSubmission = {
  operationId?: number;
  txHash?: string;
  toastId: string;
  settle: (receipt: PendingTransaction) => void;
};

const isPendingSubmission = (
  error: unknown
): error is {
  transactionPending: true;
  transactionHash?: string;
  operationId?: number;
} =>
  !!error &&
  typeof error === "object" &&
  "transactionPending" in error &&
  error.transactionPending === true;

/** The user undid the action before anything was sent: not a failure. */
const isCancelledSubmission = (
  error: unknown
): error is { cancelled: true; message?: string } =>
  !!error &&
  typeof error === "object" &&
  "cancelled" in error &&
  error.cancelled === true;

/** How long the neutral "nothing was sent" notice stays. */
const CANCELLED_TOAST_MS = 3_000;

export type TxButtonProps<T extends ElementType = "button"> = ButtonProps<T> & {
  accountProvider: AccountProvider;
  msg: Msg | null;
  SimulationComponent?: ElementType<{
    simulation?: Simulation;
    error?: Error;
  }>;
  /** Review details that must remain visible before confirmation, even when
   * the optional network-fee simulation line is hidden by a product. */
  ReviewComponent?: ElementType<SimulationComponentProps>;
  /** Refresh a failed review before allowing the user to confirm again. */
  refreshSimulationOnError?: boolean;
  /** Pause estimates during a shared account operation, then refresh the
   * review even if the form's message has not changed. */
  simulationPaused?: boolean;
  /** Quote only after an explicit click, then submit that clicked snapshot only
   * when this policy accepts it. Returning false hands off to a separate review.
   * Quote refreshes and failures never submit or retry automatically. */
  simulateOnClick?: (simulation: Simulation, message: Msg) => boolean;
  /** Read-only reconciliation of an uncertain submission; never retries it. */
  pendingTransaction?: PendingTransaction;
  /** What a quote depends on besides the message's own data, such as the
   * signing account or session. A change needs a new quote even when an
   * identical message is rendered again. */
  simulationScope?: string;
  onError?: (err: any) => void;
  /** The user undid the action before anything was sent (instead of onError). */
  onCancel?: () => void;
  onSuccess?: (res: TxResult) => void;
  onSimulation?: (simulationState: SimulationState) => void;
  hideSimulation?: boolean;
  animateSimulation?: boolean;
  assetSymbol?: string;
  // Set false for msgs that don't follow user input
  debounceSimulation?: boolean;
  toastOpts?: {
    loading?: Renderable;
    /** Replaces the spinner of the loading toast. */
    loadingIcon?: Renderable;
    /** Replaces the icons of the success and the error toast. */
    successIcon?: Renderable;
    errorIcon?: Renderable;
    success?: Renderable | ((res: TxResult) => Renderable);
    error?: Renderable | ((err: any) => Renderable);
  };
};

// Add chains here that do not require the extra reset step, for USDT allowance approvals,
// when there is already a non-zero allowance approved. We default to the 3-step process
// to avoid failing deposits for future chain integrations.
const TWO_STEP_ALLOWANCE_CHAINS = new Set<Network>([BSC, AVAX]);

// Signer adapters may throw before returning a promise (for example, when an
// address cannot be matched to a connected wallet). Execute immediately while
// converting those synchronous failures into promise rejections so callers can
// handle both failure modes through the same path.
const runAsPromise = <T,>(operation: () => T | PromiseLike<T>): Promise<T> =>
  new Promise((resolve) => resolve(operation()));

const mergeSimulations = (...values: Simulation[]): Simulation[] =>
  values.reduce<Simulation[]>((result, simulation) => {
    const index = result.findIndex(
      (item) =>
        item.symbol === simulation.symbol &&
        item.decimals === simulation.decimals
    );
    if (index === -1) return [...result, simulation];
    const current = result[index];
    return result.map((item, itemIndex) =>
      itemIndex === index
        ? {
            ...simulation,
            amount: current.amount + simulation.amount,
            gas: current.gas + simulation.gas,
          }
        : item
    );
  }, []);

const simulationFees = (state: SimulationState | undefined): Simulation[] =>
  state?.simulations || (state?.simulation ? [state.simulation] : []);

export const TxButton: FC<TxButtonProps> = (props) => {
  const { t } = useTranslation("common");
  const {
    accountProvider,
    msg,
    onSuccess,
    onError,
    onCancel,
    onSimulation,
    SimulationComponent = DefaultSimulationComponent,
    ReviewComponent,
    refreshSimulationOnError = false,
    simulationPaused = false,
    simulateOnClick,
    pendingTransaction,
    simulationScope,
    children,
    toastOpts = {},
    className,
    hideSimulation,
    animateSimulation = true,
    assetSymbol,
    debounceSimulation = true,
    onClick,
    ...rest
  } = props;

  const [simulation, setSimulation] = useState<Simulation>();
  const [simulatedMessageKey, setSimulatedMessageKey] = useState<string>();
  const [simulationError, setSimulationError] = useState<Error>();
  const [isSimulating, setIsSimulating] = useState(false);
  const simulationSeqRef = useRef(0);
  const simulationPausedRef = useRef(simulationPaused);
  simulationPausedRef.current = simulationPaused;
  const signingInFlightRef = useRef(false);
  const pendingSubmissionRef = useRef<PendingSubmission>();
  const mountedRef = useRef(true);
  const pendingTransactionRef = useRef(pendingTransaction);
  pendingTransactionRef.current = pendingTransaction;
  const toastScope = useId();
  const toastSequence = useRef(0);
  const [awaitingReceipt, setAwaitingReceipt] = useState(false);
  const currentMsgRef = useRef(msg);
  currentMsgRef.current = msg;
  const { loading, loadingIcon, successIcon, errorIcon, success, error } =
    toastOpts;
  const [isSigning, setIsSigning] = useState(false);
  // The review keeps the quote a submission was confirmed with until it
  // settles. Display only: every new submission needs its own quote.
  const [submittedSimulation, setSubmittedSimulation] = useState<Simulation>();
  const [isSuccess, setIsSuccess] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [allowanceWasReset, setAllowanceWasReset] = useState(false);
  const [allowanceSimulation, setAllowanceSimulation] = useState<{
    messageKey: string;
    status: SimulationState["status"];
  }>();
  const allowanceFeesRef = useRef<Simulation[]>([]);
  const messageKey = useMemo(() => {
    const key = transactionMessageKey(msg);
    return key === null || simulationScope === undefined
      ? key
      : `${key}@${simulationScope}`;
  }, [msg, simulationScope]);
  const messageKeyRef = useRef(messageKey);
  messageKeyRef.current = messageKey;

  // Callers commonly pass an inline lambda, so `onSimulation` is a new value on
  // every render. Read it through a ref instead of making it an effect dependency,
  // which would restart the debounce below on every render and the simulation would never
  // fire on a view that re-renders faster than the timeout.
  const onSimulationRef = useRef(onSimulation);
  useEffect(() => {
    onSimulationRef.current = onSimulation;
  });

  useEffect(() => {
    const pending = pendingSubmissionRef.current;
    if (!pending || !pendingTransaction) return;
    if (
      pending.txHash &&
      pendingTransaction.txHash &&
      pending.txHash !== pendingTransaction.txHash
    )
      return;
    // The session provides an operation id even when submission lost its hash.
    // Never consume a receipt from a later operation or another account.
    if (
      pending.operationId === undefined &&
      pendingTransaction.status === "pending"
    )
      pending.operationId = pendingTransaction.id;
    if (pending.operationId !== pendingTransaction.id) return;
    if (pendingTransaction.status === "pending") return;
    if (pendingTransaction.status === "confirmed" && !pendingTransaction.txHash)
      return;
    pendingSubmissionRef.current = undefined;
    setAwaitingReceipt(false);
    pending.settle(pendingTransaction);
  }, [pendingTransaction, awaitingReceipt]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const pending = pendingSubmissionRef.current;
      if (pending) toast.dismiss(pending.toastId);
      pendingSubmissionRef.current = undefined;
    };
  }, []);

  const doSimulate = (msg: Msg | null, key = transactionMessageKey(msg)) => {
    if (simulateOnClick) return;
    if (simulationPausedRef.current || signingInFlightRef.current) return;
    setSimulationError(undefined);
    setSimulation(undefined);
    setSimulatedMessageKey(undefined);
    onSimulationRef.current?.({ simulation: undefined, status: "none" });
    if (msg) {
      const seq = ++simulationSeqRef.current;
      setIsSimulating(true);
      onSimulationRef.current?.({ simulation: undefined, status: "running" });
      runAsPromise(() =>
        accountProvider.signer(msg.account.address).simulate(msg)
      )
        .then((sim) => {
          //ignore stale simulations
          if (seq !== simulationSeqRef.current) return;

          setIsSimulating(false);
          setSimulation(sim);
          setSimulatedMessageKey(key || undefined);
          const simulations = mergeSimulations(
            ...allowanceFeesRef.current,
            sim
          );
          const combinedSimulation = simulations.find(
            (item) =>
              item.symbol === sim.symbol && item.decimals === sim.decimals
          );
          onSimulationRef.current?.({
            simulation: combinedSimulation,
            simulations,
            status: "completed",
          });
        })
        .catch((err) => {
          //ignore stale simulation errors
          if (seq !== simulationSeqRef.current) return;

          setIsSimulating(false);
          console.error(err);
          setSimulationError(err);
          onError && onError(new TxError(msg, err, "simulation"));
          onSimulationRef.current?.({
            simulation: undefined,
            status:
              err instanceof InsufficientAllowanceError ? "running" : "failed",
          });
        });
    }
  };

  useEffect(() => {
    // Invalidate an in-flight quote immediately when the transaction changes,
    // not only after the debounce. A result for the previous transaction is
    // never a review of the next message.
    simulationSeqRef.current += 1;
    allowanceFeesRef.current = [];
    setAllowanceSimulation(undefined);
    setAllowanceWasReset(false);
    setIsSimulating(false);
    setSimulation(undefined);
    setSimulatedMessageKey(undefined);
    setSimulationError(undefined);
    onSimulationRef.current?.({ simulation: undefined, status: "none" });
    if (simulationPaused || simulateOnClick) return;
    if (!debounceSimulation) {
      doSimulate(msg, messageKey);
      return () => {
        simulationSeqRef.current += 1;
      };
    }
    const timeout = setTimeout(() => doSimulate(msg, messageKey), 1000);
    return () => {
      clearTimeout(timeout);
      simulationSeqRef.current += 1;
    };
  }, [messageKey, debounceSimulation, simulationPaused, !!simulateOnClick]);

  // A quote belongs to the transaction data it was made for (see
  // transactionMessageKey), not to a render's message object.
  const currentSimulation =
    simulatedMessageKey !== undefined && simulatedMessageKey === messageKey
      ? simulation
      : undefined;

  const handleSign = async (e: any) => {
    if (
      simulationPausedRef.current ||
      signingInFlightRef.current ||
      isSigning ||
      isSuccess ||
      isRejected ||
      rest.disabled
    )
      return;
    if (!msg) throw new Error(`Msg required`);
    if (!simulateOnClick && !currentSimulation)
      throw new Error(`Simulation required`);

    signingInFlightRef.current = true;
    setIsSigning(true);
    let submissionSimulation = currentSimulation;
    if (simulateOnClick) {
      setSimulationError(undefined);
      onSimulationRef.current?.({ simulation: undefined, status: "running" });
      try {
        // Deliberately retain this click's message through table refreshes.
        // The policy must also reject a changed account/session before signing.
        submissionSimulation = await runAsPromise(() =>
          accountProvider.signer(msg.account.address).simulate(msg)
        );
        if (!mountedRef.current || simulationPausedRef.current) {
          signingInFlightRef.current = false;
          if (mountedRef.current) setIsSigning(false);
          return;
        }
        onSimulationRef.current?.({
          simulation: submissionSimulation,
          status: "completed",
        });
        if (simulateOnClick(submissionSimulation, msg) !== true) {
          signingInFlightRef.current = false;
          setIsSigning(false);
          return;
        }
      } catch (cause) {
        signingInFlightRef.current = false;
        if (!mountedRef.current) return;
        setIsSigning(false);
        const error = cause instanceof Error ? cause : new Error(String(cause));
        setSimulationError(error);
        onSimulationRef.current?.({ simulation: undefined, status: "failed" });
        void runAsPromise(() =>
          onError?.(new TxError(msg, error, "simulation"))
        ).catch((callbackError) => {
          console.error("Transaction error callback failed", callbackError);
        });
        return;
      }
    }
    const toastId = `transaction-${toastScope}-${++toastSequence.current}`;
    const submittedKey = messageKey;
    let completedAction = true;
    setSubmittedSimulation(submissionSimulation);
    const p = runAsPromise(() => {
      const signer = accountProvider.signer(msg.account.address);
      if (onClick) onClick(e);
      return signer.signAndBroadcast(submissionSimulation!, msg);
    })
      .catch((err) => {
        if (!isPendingSubmission(err)) throw err;
        // Submission may already be on-chain. Wait for a read-only receipt check;
        // do not show rejection, refresh its quote or enable another submission.
        return new Promise<TxResult>((resolve, reject) => {
          if (!mountedRef.current) {
            // The session's top-level receipt remains available after navigation.
            toast.dismiss(toastId);
            return;
          }
          const current = pendingTransactionRef.current;
          pendingSubmissionRef.current = {
            operationId:
              err.operationId ??
              (current?.status === "pending" ? current.id : undefined),
            txHash: err.transactionHash,
            toastId,
            settle: (receipt) => {
              if (receipt.status === "failed") {
                reject(new Error(receipt.message || "Transaction failed"));
                return;
              }
              completedAction = receipt.finalTransaction !== false;
              resolve({
                address: msg.account.address,
                network: msg.account.network,
                txHash: receipt.txHash!,
                deposited: null,
                ...(!completedAction && {
                  label:
                    receipt.message ||
                    "Your transaction has not been submitted. Review it to continue.",
                }),
              });
            },
          };
          setAwaitingReceipt(true);
        });
      })
      .then(
        (res) => {
          signingInFlightRef.current = false;
          setIsSigning(false);
          setSubmittedSimulation(undefined);
          if (!completedAction) {
            // A preparatory consolidation succeeded, not the requested order.
            // Require a fresh review and explicit confirmation of that action.
            doSimulate(currentMsgRef.current, messageKeyRef.current);
            return res;
          }
          setIsSuccess(true);
          setTimeout(() => {
            setIsSuccess(false);
          }, 3000);
          // A notification, modal or balance refresh is not part of broadcasting.
          // Its failure must never change a confirmed receipt into a rejection.
          void runAsPromise(() => onSuccess?.(res)).catch((cause) => {
            console.error(
              "Transaction succeeded, but its completion callback failed",
              cause
            );
          });
          return res;
        },
        (err) => {
          signingInFlightRef.current = false;
          setIsSigning(false);
          setSubmittedSimulation(undefined);
          if (isCancelledSubmission(err)) {
            // Undone before anything was sent: no failure state. The reviewed
            // quote was used up, so a fresh one loads quietly for next time.
            if (!simulateOnClick && messageKeyRef.current === submittedKey) {
              setSimulation(undefined);
              setSimulatedMessageKey(undefined);
              doSimulate(msg, submittedKey);
            }
            void runAsPromise(() => onCancel?.()).catch((cause) => {
              console.error("Transaction cancel callback failed", cause);
            });
            throw err;
          }
          console.error(err);

          setIsRejected(true);
          if (
            refreshSimulationOnError &&
            messageKeyRef.current === submittedKey
          ) {
            setSimulation(undefined);
            setSimulatedMessageKey(undefined);
            setSimulationError(
              err instanceof Error ? err : new Error(String(err))
            );
          }
          setTimeout(() => {
            setIsRejected(false);
            if (
              refreshSimulationOnError &&
              messageKeyRef.current === submittedKey
            )
              doSimulate(msg, submittedKey);
          }, 3000);

          void runAsPromise(() =>
            onError?.(new TxError(msg, err, "signAndBroadcast"))
          ).catch((cause) => {
            console.error("Transaction error callback failed", cause);
          });

          throw err;
        }
      );

    // An undone action ends with a short neutral notice instead of an error.
    const settled = p.catch((err) => {
      if (!isCancelledSubmission(err)) throw err;
      toast(err.message || "Cancelled. Nothing was sent.", {
        id: toastId,
        icon: null,
        duration: CANCELLED_TOAST_MS,
      });
      // Leave that notice in place of a success or an error toast.
      return new Promise<never>(() => undefined);
    });

    toast.promise(
      settled,
      {
        loading: loading || t("submittingTransaction"),
        success: (res: TxResult) => {
          try {
            if (!completedAction) return res.label!;
            return typeof success === "function"
              ? success(res)
              : success || successHandler(res, t("transactionSucceeded"));
          } catch (cause) {
            // toast.promise routes formatter errors to its error toast too.
            console.error("Transaction success notification failed", cause);
            return t("transactionSucceeded");
          }
        },
        error:
          error ||
          ((err: any) => {
            console.error(err);
            const defaultError = t("errorSubmittingTransaction");
            switch (typeof err) {
              case "string":
                return translateError(err) || defaultError;
              case "object":
                return err && "message" in err
                  ? translateError(err.message) || defaultError
                  : defaultError;

              default:
                return defaultError;
            }
          }),
      },
      {
        id: toastId,
        ...(loadingIcon === undefined
          ? {}
          : { loading: { icon: loadingIcon } }),
        ...(successIcon === undefined
          ? {}
          : { success: { icon: successIcon } }),
        ...(errorIcon === undefined ? {} : { error: { icon: errorIcon } }),
      }
    );
  };

  const disabled =
    simulationPaused ||
    isSigning ||
    isSuccess ||
    isRejected ||
    !msg ||
    (!simulateOnClick && (!currentSimulation || !!simulationError)) ||
    rest.disabled;

  // Separate spacing classes (always applied) from styling classes (conditionally applied)
  const { spacing: spacingClasses, styling: stylingClasses } =
    separateClasses(className);

  const gasSymbol = useMemo(() => {
    if (!msg) return undefined;
    try {
      return gasToken(msg.account.network).symbol;
    } catch {
      return undefined;
    }
  }, [msg]);

  const trackAllowanceSimulation = (
    allowanceMsg: Msg,
    state: SimulationState
  ) => {
    if (state.status === "completed") {
      allowanceFeesRef.current = mergeSimulations(
        ...allowanceFeesRef.current,
        ...simulationFees(state)
      );
    }
    setAllowanceSimulation({
      messageKey: transactionMessageKey(allowanceMsg) || "",
      status: state.status,
    });
    // Approval-only fees are incomplete. Keep consumers pending until the
    // original transaction is simulated and both fee stages can be reported.
    onSimulationRef.current?.(
      state.status === "failed"
        ? state
        : { simulation: undefined, status: "running" }
    );
  };
  const insufficientGas = Boolean(
    gasSymbol &&
      simulationError?.message.match(/(insufficient|not enough).*(gas|fee)/i)
  );
  const gasTooltip = insufficientGas
    ? assetSymbol?.toUpperCase() === gasSymbol?.toUpperCase()
      ? t("insufficientGasTokenFeeTooltip", { symbol: gasSymbol })
      : t("insufficientTokenFeeTooltip", { symbol: gasSymbol })
    : undefined;
  const tooltip =
    gasTooltip ||
    (simulationError?.message
      ? translateError(simulationError.message)
      : undefined);

  const allowanceMsg = useMemo(() => {
    if (!msg) return null;
    if (!simulationError) return null;
    if (!(simulationError instanceof InsufficientAllowanceError)) return null;
    return new MsgErc20IncreaseAllowance(msg.account, simulationError);
  }, [msg, simulationError]);

  const resetAllowanceMsg = useMemo(() => {
    if (!msg) return null;
    if (!simulationError) return null;
    if (!(simulationError instanceof InsufficientAllowanceError)) return null;
    if (simulationError.current === 0n) return null;
    if (simulationError.asset.symbol.toUpperCase() !== "USDT") return null;
    if (TWO_STEP_ALLOWANCE_CHAINS.has(msg.account.network)) return null;
    return new MsgErc20ResetAllowance(msg.account, simulationError);
  }, [msg, simulationError]);
  const resetAllowanceMessageKey = transactionMessageKey(resetAllowanceMsg);
  const allowanceMessageKey = transactionMessageKey(allowanceMsg);

  if (resetAllowanceMsg)
    return (
      <>
        <TxButton
          {...props}
          toastOpts={{
            loading: t("resettingAllowance"),
            success: t("allowanceReset"),
          }}
          label={t("stepReset")}
          msg={resetAllowanceMsg}
          onSuccess={() => {
            setAllowanceWasReset(true);
            doSimulate(msg, messageKey);
          }}
          onSimulation={(state) =>
            trackAllowanceSimulation(resetAllowanceMsg, state)
          }
        />
        {allowanceSimulation?.messageKey === resetAllowanceMessageKey &&
          allowanceSimulation.status === "completed" && (
            <ResetAllowanceWarning
              error={simulationError as InsufficientAllowanceError}
            />
          )}
      </>
    );

  if (allowanceMsg)
    return (
      <>
        <TxButton
          {...props}
          toastOpts={{
            loading: t("increasingAllowance"),
            success: t("allowanceIncreased"),
          }}
          label={allowanceWasReset ? t("stepApprove2of3") : t("stepApprove")}
          msg={allowanceMsg}
          onSuccess={() => doSimulate(msg, messageKey)}
          onSimulation={(state) =>
            trackAllowanceSimulation(allowanceMsg, state)
          }
        />
        {allowanceSimulation?.messageKey === allowanceMessageKey &&
          allowanceSimulation.status === "completed" && (
            <InsufficientAllowanceWarning isStep2={allowanceWasReset} />
          )}
      </>
    );

  // Receipt/signing feedback takes precedence over a background fee refresh.
  // Private reviews also stay in the existing loading state during debounce,
  // instead of briefly showing a grey action button between quote requests.
  const showSimulation =
    animateSimulation &&
    !simulationPaused &&
    !isSigning &&
    !isSuccess &&
    !isRejected &&
    (isSimulating ||
      (!!ReviewComponent && !!msg && !currentSimulation && !simulationError));
  const showWaiting = isSigning || isRejected || isSuccess || showSimulation;
  // Receipt and signing states outrank a quote refreshing in the background.
  const statusIcon = isSuccess
    ? "tx-success"
    : isRejected
      ? "tx-fail"
      : isSigning
        ? "tx-sign"
        : showSimulation
          ? "tx-simulate"
          : "exclamation";

  const modProps = {
    ...rest,
    ...(rest.label && {
      label: `${
        isSuccess
          ? t("success")
          : isSigning
            ? t("waiting")
            : isRejected
              ? t("rejected")
              : showSimulation
                ? t("simulating")
                : insufficientGas
                  ? t("insufficientFee", { symbol: gasSymbol })
                  : rest.label
      }`,
    }),
  };
  return (
    <>
      {ReviewComponent && msg && (
        <ReviewComponent
          simulation={currentSimulation ?? submittedSimulation}
          error={simulationError}
          isSimulating={isSimulating}
          isPaused={simulationPaused || isSigning}
        />
      )}
      <Button
        {...modProps}
        onClick={handleSign}
        className={clsx(
          stylingClasses.length > 0 ? stylingClasses : "button",
          spacingClasses,
          {
            "button--grey":
              disabled &&
              !showWaiting &&
              !isSimulating &&
              !stylingClasses.includes("transparent"),
            "button--waiting":
              showWaiting && !className?.includes("transparent"),
            "button--success": isSuccess,
            "button--rejected": isRejected,
            "button--simulating": showSimulation,
          }
        )}
        style={{
          cursor: showWaiting ? "default" : undefined,
          ...rest.style,
        }}
        disabled={disabled}
        data-tooltip-id="tx-button-tip"
        data-tooltip-html={
          tooltip
            ? `<p class="fs-12 lh-16 fw-400 w-36 mb-0.5">${tooltip}</p>`
            : undefined
        }>
        {(showWaiting || !!simulationError) &&
          !className?.includes("transparent") && (
            <Animated
              name={statusIcon}
              data-status-icon={statusIcon}
              className="w-4 h-4 color-white big"
              aria-label="lock"
            />
          )}
        {(isSigning || isRejected || isSuccess) &&
        className?.includes("transparent") ? (
          <Loader className="w-full h-full" />
        ) : (
          ((!isSigning && !isRejected && !isSuccess) ||
            className?.includes("transparent")) &&
          children
        )}
      </Button>
      {hideSimulation ? null : msg ? (
        <SimulationComponent
          simulation={currentSimulation}
          error={simulationError}
        />
      ) : null}
    </>
  );
};

export const TxButtonTip = () => (
  <Tooltip
    id="tx-button-tip"
    className="tooltip"
    float={true}
    style={{ zIndex: 203 }}
  />
);
export interface SimulationComponentProps {
  simulation?: Simulation;
  error?: Error;
  isSimulating?: boolean;
  isPaused?: boolean;
}

const DefaultSimulationComponent = ({
  simulation,
  error,
  isSimulating,
}: SimulationComponentProps): ReactElement => {
  const { t } = useTranslation("common");
  if (error instanceof InsufficientAllowanceError) {
    return <InsufficientAllowanceWarning />;
  }
  if (error) return <span>&nbsp;</span>;
  if (!simulation && !isSimulating) return <span>&nbsp;</span>;

  return (
    <small className="fs-12 text-center mt-1 color-grey block">
      <>
        {t("networkFee")}{" "}
        {simulation ? (
          <Decimal
            subscript
            amount={simulation.amount}
            decimals={simulation.decimals}
            symbol={simulation.symbol}
            className="color-white"
          />
        ) : (
          <span>{t("calculating")}</span>
        )}
      </>
    </small>
  );
};

const InsufficientAllowanceWarning = ({
  isStep2 = false,
}: {
  isStep2?: boolean;
}) => {
  const { t } = useTranslation("common");
  return (
    <Warning
      className="warning warning--borderless mt-2 condensed flex ai-c"
      color="teal">
      <Animated name="unlock" aria-label="lock" style={{ height: "2.25rem" }} />
      <div className="text-left">
        {isStep2
          ? t("insufficientAllowanceWarningStep2")
          : t("insufficientAllowanceWarning")}
      </div>
    </Warning>
  );
};

const ResetAllowanceWarning = ({
  error,
}: {
  error: InsufficientAllowanceError;
}) => {
  return (
    <Warning
      className="warning warning--borderless mt-2 condensed flex ai-c"
      color="teal">
      <Animated name="unlock" aria-label="lock" style={{ height: "2.25rem" }} />
      <div className="text-left">
        <Trans
          i18nKey="common:resetAllowanceWarning"
          components={{
            allowance: (
              <Decimal
                amount={error.current}
                decimals={error.asset.decimals}
                symbol={error.asset.symbol}
                className="color-white"
              />
            ),
          }}
        />
      </div>
    </Warning>
  );
};

const successHandler = (res: TxResult, defaultLabel: string): Renderable =>
  res.label ? (
    <p>{res.label}</p>
  ) : (
    <p>
      {defaultLabel}
      <br />
      <a
        href={networkTxLink(res)}
        target="_blank"
        className="color-white no-underline fs-12">
        {res.txHash.slice(0, 8) + "..." + res.txHash.slice(-8)}
      </a>
    </p>
  );
